import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    GridItem,
    HStack,
    Icon,
    IconButton,
    Input,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {FaTrash} from "react-icons/fa";
import {withMask} from "use-mask-input";
import {AuthContext} from "../../../../contexts/AuthContext";
import {boatService} from "../../../../services/boatService";

function BoatObservationsForm({onSubmit, onBack, values}) {

    const {t} = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const agentColor = useColorModeValue("gray.500", "white");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const {user} = useContext(AuthContext);

    const ids = useMemo(() => ({
        socorrista: "socorrista",
        nome: "nome",
        documentoIdentificacao: "documentoIdentificacao",
        endereco: "endereco",
        telefone: "telefone",
        observacao: "observacao",
        agenteTransito: "agenteTransito",
        agenteId: "agenteId",
        matricula: "matricula",
        agentesEnvolvidos: "agentesEnvolvidos"
    }));

    const initFormData = (data) => {
        return {
            socorrista: {
                nome: data?.socorrista?.nome || '',
                documentoIdentificacao: data?.socorrista?.documentoIdentificacao || '',
                endereco: data?.socorrista?.endereco || '',
                telefone: data?.socorrista?.telefone || '',
                observacao: data?.socorrista?.observacao || '',
            },
            agenteTransito: {
                nome: data?.agenteTransito?.nome || '',
                matricula: data?.agenteTransito?.matricula || '',
                observacao: data?.agenteTransito?.observacao || ''
            },
            agentesEnvolvidos: data?.agentesEnvolvidos || []
        }
    };

    const [formData, setFormData] = useState(initFormData(values));
    const [listOfAgents, setListOfAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);

    useEffect(() => {
        boatService.getAgents().then((agents) => {
            agents.sort((a, b) => a.description.localeCompare(b.description));
            setListOfAgents(agents);
        });
    }, []);

    const handleInputChange = (e) => {
        const idTokens = e.target.id.split("-");
        const value = e.target.value;

        const key = idTokens[0];
        const att = idTokens[1];

        formData[key][att] = value;

        const newFormData = {...formData};
        setFormData(newFormData);
    };

    const handleSelectedAgent = (e) => {
        const value = e.target.value;

        const agent = listOfAgents.find((agent) => agent.id === Number(value));

        setSelectedAgent(agent);
    }

    const addAgent = () => {
        if (formData[ids.agentesEnvolvidos].some(ag => ag.agenteId === selectedAgent.id)) {
            return;
        }

        if (selectedAgent) {
            formData[ids.agentesEnvolvidos].push({
                nome: selectedAgent.description,
                matricula: selectedAgent.codigo,
                agenteId: selectedAgent.id
            });

            const newFormData = {...formData};
            setFormData(newFormData);
        }
    }

    const removeAgent = (index) => {
        formData[ids.agentesEnvolvidos].splice(index, 1);

        const newFormData = {...formData};
        setFormData(newFormData);
    }

    const handleOnSubmit = () => {
        submit();
    }

    const saveDraft = () => {
        submit(true)
    }

    const submit = (isDraft = false) => {
        if (!formData[ids.agenteTransito][ids.nome]) {
            formData[ids.agenteTransito][ids.nome] = user?.pessoa?.nome;
            formData[ids.agenteTransito][ids.matricula] = user?.agente_matricula;
        }

        onSubmit(formData, isDraft);
    }

    return (
        <>
            <Stack>
                <Text fontWeight="bold">{t('boat.new.observations.agent')}</Text>

                <Stack mt="10px">
                    <Text color={agentColor} fontSize="sm">{user?.agente_matricula} - {user?.pessoa?.nome}</Text>

                    <FormControl>
                        <Textarea
                            fontSize="sm"
                            variant="main"
                            id={`${[ids.agenteTransito]}-${[ids.observacao]}`}
                            value={formData[ids.agenteTransito][ids.observacao]}
                            onChange={handleInputChange}
                            placeholder={t("boat.new.observations.title")}
                            _placeholder={{color: placeholderColor}}
                        />
                    </FormControl>
                </Stack>

                <Text fontWeight="bold" mt="10px">{t('boat.new.observations.agents')}</Text>

                <Stack mt="10px">
                    <FormControl>
                        <Select
                            fontSize="sm"
                            variant="main"
                            id={ids.agentes}
                            onChange={handleSelectedAgent}
                            placeholder={t(`boat.new.observations.agents`)}
                            _placeholder={{color: placeholderColor}}
                        >
                            {listOfAgents.map((agent) => (
                                <option key={agent.id} value={agent.id}>
                                    {agent.codigo} - {agent.description}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="light"
                        fontSize="sm"
                        w="100px"
                        onClick={addAgent}
                    >
                        {t("system.actions.add")}
                    </Button>
                    <Stack>
                        {formData[ids.agentesEnvolvidos]?.map((agent, index) => (
                            <HStack key={`agent-${index}`}>
                                <Text>{agent.matricula} - {agent.nome}</Text>
                                <IconButton
                                    size="xs"
                                    colorScheme="red"
                                    aria-label={t("system.actions.clear")}
                                    onClick={(e) => removeAgent(index)}
                                    icon={<Icon as={FaTrash}/>}
                                />
                            </HStack>
                        ))};
                    </Stack>
                </Stack>

                <Text fontWeight="bold" mt="20px">{t('boat.new.observations.rescuer.title')}</Text>

                <SimpleGrid spacing={2} columns={{sm: 1, md: 4}} mt="10px">
                    <GridItem colSpan={2}>
                        <FormControl>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("boat.new.observations.rescuer.name")}
                            </FormLabel>
                            <Input
                                fontSize="sm"
                                variant="main"
                                id={`${[ids.socorrista]}-${[ids.nome]}`}
                                value={formData[ids.socorrista][ids.nome]}
                                onChange={handleInputChange}
                                placeholder={t('boat.new.observations.rescuer.name')}
                                _placeholder={{color: placeholderColor}}
                            />
                        </FormControl>
                    </GridItem>
                    <FormControl>
                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                            {t("boat.new.observations.rescuer.id")}
                        </FormLabel>
                        <Input
                            fontSize="sm"
                            variant="main"
                            id={`${[ids.socorrista]}-${[ids.documentoIdentificacao]}`}
                            value={formData[ids.socorrista][ids.documentoIdentificacao]}
                            onChange={handleInputChange}
                            placeholder={t('boat.new.observations.rescuer.id')}
                            _placeholder={{color: placeholderColor}}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                            {t("boat.new.observations.rescuer.phone")}
                        </FormLabel>
                        <Input
                            fontSize="sm"
                            variant="main"
                            id={`${[ids.socorrista]}-${[ids.telefone]}`}
                            value={formData[ids.socorrista][ids.telefone]}
                            onChange={handleInputChange}
                            placeholder={t('boat.new.observations.rescuer.phone')}
                            _placeholder={{color: placeholderColor}}
                            ref={withMask("(99) [9]9999-9999")}
                        />
                    </FormControl>
                </SimpleGrid>

                <SimpleGrid spacing={2} columns={1} mt="10px">
                    <FormControl>
                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                            {t("boat.new.observations.rescuer.address")}
                        </FormLabel>
                        <Input
                            fontSize="sm"
                            variant="main"
                            id={`${[ids.socorrista]}-${[ids.endereco]}`}
                            value={formData[ids.socorrista][ids.endereco]}
                            onChange={handleInputChange}
                            placeholder={t('boat.new.observations.rescuer.address')}
                            _placeholder={{color: placeholderColor}}
                        />
                    </FormControl>
                </SimpleGrid>

                <SimpleGrid spacing={2} columns={1} mt="10px">
                    <FormControl>
                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                            {t("boat.new.observations.title")}
                        </FormLabel>
                        <Textarea
                            fontSize="sm"
                            variant="main"
                            id={`${[ids.socorrista]}-${[ids.observacao]}`}
                            value={formData[ids.socorrista][ids.observacao]}
                            onChange={handleInputChange}
                            placeholder={t("boat.new.observations.title")}
                            _placeholder={{color: placeholderColor}}
                        />
                    </FormControl>
                </SimpleGrid>

            </Stack>

            <Flex w="100%" justify="space-between" mt="20px">
                <Button
                    variant="light"
                    fontSize="sm"
                    w="125px"
                    onClick={onBack}
                >
                    {t("system.actions.back")}
                </Button>
                <Flex>
                    <Button
                        w="150px"
                        variant="solid"
                        fontSize="sm"
                        mr="10px"
                        onClick={saveDraft}
                    >
                        {t('boat.actions.draft')}
                    </Button>
                    <Button
                        w="125px"
                        variant="dark"
                        fontSize="sm"
                        onClick={handleOnSubmit}
                    >
                        {t('system.actions.next')}
                    </Button>
                </Flex>
            </Flex>
        </>
    );
}

export default BoatObservationsForm;